import { useState, useCallback, useRef } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import type { HealthCheckResponse, SystemInfo, ServiceInfo } from '../types';

export function useSystemHealth() {
  const httpClient = useHttpClient();

  // State
  const [healthCheck, setHealthCheck] = useState<HealthCheckResponse | null>(null);
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [services, setServices] = useState<ServiceInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-refresh interval ref
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Fetch health check data
   */
  const fetchHealthCheck = useCallback(async () => {
    try {
      // Health endpoint returns raw object, not ResponseData wrapped
      // Use getRaw instead of get to skip ResponseData unwrapping
      const response = await httpClient.getRaw<any>(getApiUrl('system', '/api/health/detailed'));

      // Transform raw health response to HealthCheckResponse format
      const healthCheckResponse: HealthCheckResponse = {
        status: response.Status || 'Unknown',
        entries: {}, // Health endpoint doesn't provide detailed entries yet
      };

      setHealthCheck(healthCheckResponse);
    } catch (err: any) {
      console.error('Health check error:', err);
      throw err;
    }
  }, [httpClient]);

  /**
   * Fetch system info
   */
  const fetchSystemInfo = useCallback(async () => {
    try {
      // Health endpoint returns raw object, not ResponseData wrapped
      // Use getRaw instead of get to skip ResponseData unwrapping
      const response = await httpClient.getRaw<any>(getApiUrl('system', '/api/health/detailed'));

      // Transform raw health response to SystemInfo format
      const systemInfo: SystemInfo = {
        environment: response.Environment || 'Unknown',
        version: response.Version || '1.0.0',
        uptime: response.Uptime || '0:00:00',
        serverTime: response.Timestamp || new Date().toISOString(),
        machineName: response.MachineName || 'Unknown',
        osVersion: 'Linux', // Not provided by current endpoint
        dotnetVersion: '.NET 9.0', // Not provided by current endpoint
        memoryUsage: response.WorkingSet ? {
          used: parseInt(response.WorkingSet.replace(/[^\d]/g, '')),
          total: 1024, // Placeholder
          percentage: 0, // Placeholder
        } : undefined,
      };

      setSystemInfo(systemInfo);
    } catch (err: any) {
      console.error('System info error:', err);
      throw err;
    }
  }, [httpClient]);

  /**
   * Fetch services status
   */
  const fetchServices = useCallback(async () => {
    try {
      const response = await httpClient.get<any>(getApiUrl('system', '/api/system'));

      // Response is wrapped in ResponseData<List<SystemServiceModelView>>
      // Extract the Data array
      if (response && Array.isArray(response)) {
        setServices(response);
      } else {
        // Handle empty or invalid response
        setServices([]);
      }
    } catch (err: any) {
      console.error('Services error:', err);

      // If it's a 403 Forbidden error, silently set empty services
      // (User doesn't have Admin role to view services)
      if (err.statusCode === 403) {
        console.warn('User does not have permission to view system services (Admin role required)');
        setServices([]);
        // Don't throw the error, just continue
        return;
      }

      throw err;
    }
  }, [httpClient]);

  /**
   * Fetch all data (unified refresh)
   */
  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Use Promise.allSettled to allow partial success
      // (e.g., health check succeeds but services fails due to 403)
      const results = await Promise.allSettled([
        fetchHealthCheck(),
        fetchSystemInfo(),
        fetchServices(),
      ]);

      // Check if any critical errors occurred (not 403)
      const errors = results
        .filter((r) => r.status === 'rejected')
        .map((r: any) => r.reason?.message || 'Unknown error');

      if (errors.length > 0 && errors.length === results.length) {
        // All requests failed
        setError(errors.join('; '));
      } else if (errors.length > 0) {
        // Some requests failed but not all
        console.warn('Some requests failed:', errors);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch system health data');
    } finally {
      setIsLoading(false);
    }
  }, [fetchHealthCheck, fetchSystemInfo, fetchServices]);

  /**
   * Start auto-refresh
   */
  const startAutoRefresh = useCallback(
    (intervalMs: number = 30000) => {
      // Clear existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Set new interval
      intervalRef.current = setInterval(() => {
        fetchAll();
      }, intervalMs);
    },
    [fetchAll]
  );

  /**
   * Stop auto-refresh
   */
  const stopAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  return {
    // State
    healthCheck,
    systemInfo,
    services,
    isLoading,
    error,
    setError,
    // Actions
    fetchAll,
    fetchHealthCheck,
    fetchSystemInfo,
    fetchServices,
    startAutoRefresh,
    stopAutoRefresh,
  };
}
